/**
 * Every string the interface shows, in both languages.
 *
 * One flat object rather than per-component strings, so "is this translated?"
 * is answerable by reading one file — and so a test can assert that the two
 * languages have exactly the same keys, which is the bug that otherwise ships.
 */
export const en = {
  'app.name': 'PanelTunnel',
  'app.tagline': 'Set up your panel and servers without touching a terminal.',

  'nav.dashboard': 'Home',
  'nav.servers': 'My servers',
  'nav.deployments': 'Installations',
  'nav.newDeployment': 'New installation',
  'nav.signOut': 'Sign out',
  'nav.menu': 'Menu',
  'nav.close': 'Close menu',

  'common.next': 'Continue',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.retry': 'Try again',
  'common.loading': 'Loading…',
  'common.optional': 'optional',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.show': 'Show',
  'common.hide': 'Hide',
  'common.somethingWentWrong': 'Something went wrong.',
  'common.offline': 'You appear to be offline. We will keep trying.',

  'theme.label': 'Appearance',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'Match my device',
  'locale.label': 'Language',

  'mode.label': 'Mode',
  'mode.easy': 'Easy',
  'mode.expert': 'Expert',
  'mode.easyHelp': 'We choose sensible settings for you.',
  'mode.expertHelp': 'You choose the port, the tunnel and the details.',

  'auth.signIn': 'Sign in',
  'auth.signUp': 'Create an account',
  'auth.email': 'Email address',
  'auth.password': 'Password',
  'auth.displayName': 'Your name',
  'auth.organizationName': 'Company or team name',
  'auth.haveAccount': 'Already have an account?',
  'auth.needAccount': 'New here?',
  'auth.passwordHint': 'At least 10 characters. A short sentence works well.',
  'auth.welcome': 'Welcome back',
  'auth.welcomeNew': 'Let’s get you set up',

  'dashboard.greeting': 'Hello, {name}',
  'dashboard.noServers': 'You have not added a server yet.',
  'dashboard.noServersHelp':
    'Add the server you rented, and we will check it over before anything is installed.',
  'dashboard.addFirstServer': 'Add my first server',
  'dashboard.startInstall': 'Install a panel',
  'dashboard.serversCount': '{count} servers',
  'dashboard.recentDeployments': 'Recent installations',
  'dashboard.nothingYet': 'Nothing yet.',

  'servers.title': 'My servers',
  'servers.add': 'Add a server',
  'servers.name': 'A name you will recognise',
  'servers.namePlaceholder': 'e.g. Germany server',
  'servers.host': 'Address',
  'servers.hostHelp': 'The IP address or hostname your provider gave you.',
  'servers.port': 'SSH port',
  'servers.username': 'Username',
  'servers.usernameHelp': 'Usually “root”.',
  'servers.accessMethod': 'How do we sign in?',
  'servers.accessKey': 'With an SSH key',
  'servers.accessPassword': 'With a password',
  'servers.privateKey': 'Private key',
  'servers.privateKeyHelp':
    'Paste the whole key, including the first and last lines. We store it encrypted.',
  'servers.privateKeyPassphrase': 'Key passphrase',
  'servers.passwordLabel': 'Password',
  'servers.sudoPassword': 'Password for administrator commands',
  'servers.location': 'Where is it?',
  'servers.locationPlaceholder': 'e.g. Frankfurt',
  'servers.authorization': 'I own this server, or I am allowed to administer it.',
  'servers.authorizationHelp':
    'We will only ever connect to servers you tell us are yours. You can withdraw this at any time.',
  'servers.checking': 'Checking…',
  'servers.checkAgain': 'Check again',
  'servers.remove': 'Withdraw access',
  'servers.removeConfirm':
    'We will delete the sign-in details for this server. Nothing installed on it is removed.',
  'servers.removed':
    'We have deleted the access details for that server. Nothing installed on it has been removed.',
  'servers.statusDraft': 'Not checked yet',
  'servers.statusInspecting': 'Checking',
  'servers.statusReady': 'Ready',
  'servers.statusUnreachable': 'Cannot reach it',
  'servers.statusRetired': 'Access withdrawn',
  'servers.statusInUse': 'In use',
  'servers.behindNat': 'Behind a shared address (NAT)',
  'servers.existingSoftware': 'Already installed here',
  'servers.rebootPending': 'This server is waiting to be restarted. We will not restart it.',
  'servers.cannotElevate':
    'This account cannot run administrator commands. Installation needs that.',

  'wizard.title': 'New installation',
  'wizard.step': 'Step {current} of {total}',
  'wizard.step.panel': 'Choose a panel',
  'wizard.step.shape': 'Choose your setup',
  'wizard.step.servers': 'Assign your servers',
  'wizard.step.options': 'Settings',
  'wizard.step.review': 'Review',

  'wizard.panel.help': 'This is what you will log in to and manage your users from.',
  'wizard.panel.recommended': 'If you are not sure, choose {name}.',
  'wizard.panel.multiNode': 'Can manage several servers',
  'wizard.panel.singleNode': 'One server only',
  'wizard.panel.difficultyEasy': 'Simplest',
  'wizard.panel.difficultyModerate': 'Balanced',
  'wizard.panel.difficultyAdvanced': 'Most capable',
  'wizard.panel.needs': 'Needs {memory} MB memory, {cpu} CPU',
  'wizard.panel.unavailableForShape': 'Not available for the setup you chose',

  'wizard.shape.help': 'How should your servers work together?',
  'wizard.shape.needsMoreServers': 'You need {count} servers for this. You have {have}.',

  'wizard.servers.help': 'Put each server in its place. We only offer servers we have checked.',
  'wizard.servers.choose': 'Choose a server',
  'wizard.servers.noneReady': 'None of your servers has finished its check yet.',
  'wizard.servers.addMore': 'Add another server',
  'wizard.servers.alreadyUsed': 'Already used in this setup',

  'wizard.options.tunnel': 'How should your servers talk to each other?',
  'wizard.options.tunnelAutomatic': 'Choose for me (recommended)',
  'wizard.options.tunnelAutomaticHelp':
    'We test the connection between your servers and pick what works best.',
  'wizard.options.domain': 'Your domain',
  'wizard.options.domainHelp':
    'Leave this empty to use the server’s address. You can add a domain later.',
  'wizard.options.ssl': 'Get a security certificate for the domain',
  'wizard.options.panelPort': 'Panel port',
  'wizard.options.panelPortHelp': 'Leave this alone unless you have a reason to change it.',
  'wizard.options.encryptedOnly': 'Only use an encrypted connection between servers',

  'wizard.review.title': 'Here is what we will do',
  'wizard.review.estimated': 'About {minutes} minutes',
  'wizard.review.steps': '{count} steps',
  'wizard.review.tunnelChoice': 'Connection between your servers',
  'wizard.review.warnings': 'Please read before continuing',
  'wizard.review.findings': 'What we found on your servers',
  'wizard.review.credentials': 'You will receive',
  'wizard.review.approve': 'Start the installation',
  'wizard.review.approving': 'Starting…',
  'wizard.review.blocker':
    'We cannot continue until this is resolved. Nothing has been changed on your servers.',

  'progress.title': 'Setting everything up',
  'progress.subtitle': 'You can close this page. We will keep going.',
  'progress.stepRunning': 'Working on it',
  'progress.stepSuccess': 'Done',
  'progress.stepFailed': 'Did not work',
  'progress.stepPending': 'Waiting',
  'progress.stepSkipped': 'Not needed',
  'progress.reconnecting': 'Reconnecting…',
  'progress.cancel': 'Stop',
  'progress.cancelConfirm':
    'We will stop after the step that is running. Anything already installed stays.',
  'progress.cancelling': 'Stopping after the current step…',

  'result.successTitle': 'Everything is ready',
  'result.successBody': 'Your panel is installed and working. Here is how to sign in.',
  'result.failedTitle': 'We could not finish',
  'result.failedBody':
    'We stopped safely and undid what we had done. Nothing was left half-configured.',
  'result.rolledBack': 'We undid our changes.',
  'result.openPanel': 'Open my panel',
  'result.credentials': 'Your sign-in details',
  'result.credentialsUnavailable':
    'Your installation finished, but we could not load your sign-in details just now. They are safe — try again in a moment. Do not install again.',
  'result.credentialWarning':
    'Save these somewhere safe. We store them encrypted, but you should keep your own copy.',
  'result.revealOnce': 'Show password',
  'result.username': 'Username',
  'result.password': 'Password',
  'result.address': 'Address',
  'result.whatNext': 'What now?',
  'result.whatNextBody': 'Sign in to your panel and add your first user.',

  // The page a one-time link from the bot opens. It has no navigation and no
  // session: somebody arriving here has a link and nothing else.
  'access.title': 'Your panel access',
  'access.body':
    'This link opens once. Save these details somewhere safe before you close this page.',
  'access.expired':
    'This link has already been used, or it has expired. Ask the bot for a new one — it can make another whenever you need it.',
  'access.loading': 'Opening your details…',
  'access.none': 'This installation has no stored sign-in details.',
  'access.notHeld': 'Not stored by PanelTunnel — sign in with the password you already use.',
  'access.panelHost': 'Panel server',
  'access.via': 'Reached through',
  'access.viaEntryRelay':
    'the entry relay {host} (an access path; the panel is not installed there)',

  'deployments.title': 'Installations',
  'deployments.empty': 'You have not installed anything yet.',
  'deployments.statusDraft': 'Draft',
  'deployments.statusPlanned': 'Waiting for your approval',
  'deployments.statusQueued': 'Queued',
  'deployments.statusRunning': 'In progress',
  'deployments.statusVerifying': 'Checking it works',
  'deployments.statusSucceeded': 'Working',
  'deployments.statusFailed': 'Did not work',
  'deployments.statusCancelled': 'Stopped',
  'deployments.statusRolledBack': 'Undone',
  'deployments.statusPartiallyRolledBack': 'Partly undone',
  'deployments.statusCancelling': 'Stopping',
  'deployments.view': 'View',

  'error.network': 'We could not reach the server. Check your connection and try again.',
  'error.rateLimited': 'Too many attempts. Please wait a minute and try again.',

  'nav.backups': 'Backups',
  'nav.monitoring': 'Monitoring',
  'nav.settings': 'Settings',

  'common.on': 'On',
  'common.off': 'Off',
  'common.never': 'Never',
  'common.unknown': 'Not known',
  'common.refresh': 'Refresh',
  'common.disable': 'Turn off',
  'common.technical': 'Technical details',

  'health.healthy': 'Working',
  'health.degraded': 'Working, with problems',
  'health.unhealthy': 'Not working',
  'health.unknown': 'Not checked yet',
  'health.lastChecked': 'Last checked {when}',
  'health.neverChecked': 'We have not checked this yet.',

  'backups.title': 'Backups',
  'backups.subtitle': 'A copy of your panel’s users and settings, kept encrypted.',
  'backups.optIn':
    'We never take backups on a schedule unless you turn them on. You can still take one whenever you like.',
  'backups.noPanels': 'You have no installed panel to back up yet.',
  'backups.scheduleOff': 'Scheduled backups are off',
  'backups.scheduleOn': 'A backup is taken every {hours} hours',
  'backups.nextRun': 'Next one {when}',
  'backups.every': 'How often',
  'backups.every6': 'Every 6 hours',
  'backups.every12': 'Every 12 hours',
  'backups.every24': 'Once a day',
  'backups.every168': 'Once a week',
  'backups.keep': 'How many to keep',
  'backups.keepHelp': 'Older backups beyond this are deleted after a successful new one.',
  'backups.turnOnConfirm': 'Turn on scheduled backups',
  'backups.turnOffConfirm':
    'We will stop taking new backups on a schedule. The backups you already have are kept.',
  'backups.now': 'Back up now',
  'backups.queued': 'We are taking a backup. It will appear here when it is done.',
  'backups.history': 'Backups you have',
  'backups.empty': 'No backups yet.',
  'backups.taken': 'Taken',
  'backups.size': 'Size',
  'backups.reason': 'Why',
  'backups.reasonRequested': 'You asked for it',
  'backups.reasonScheduled': 'On schedule',
  'backups.reasonRestore': 'Before a restore',
  'backups.verify': 'Check it',
  'backups.verifyHelp': 'Opens the backup and checks it is complete. Your panel is not touched.',
  'backups.verified': 'This backup is complete and readable.',
  'backups.verifyFailed': 'We could not read this backup: {reason}',
  'backups.restore': 'Restore',
  'backups.restoreTitle': 'Restore this backup?',
  'backups.restoreWarning':
    'This replaces everything in your panel with the copy from {when}. Users added since then will be gone. We take a backup first, but there is no undo beyond that.',
  'backups.restoreType': 'Type RESTORE to confirm',
  'backups.restoreWord': 'RESTORE',
  'backups.restoreStarted': 'The restore has started. You can watch it on this page.',

  'ssl.title': 'Security certificate',
  'ssl.subtitle': 'What your browser sees when it opens your panel.',
  'ssl.trusted': 'Trusted certificate',
  'ssl.trustedHelp': 'Issued for {domains}. Browsers accept it without a warning.',
  'ssl.bootstrap': 'Internal certificate — not trusted',
  'ssl.bootstrapHelp':
    'Your panel is using a certificate we generated ourselves so it could start. Browsers do not accept it and will show a warning. It is fine for reaching the panel over the private connection between your servers; it is not fine for a public address.',
  'ssl.bootstrapAction': 'Add your domain to get a certificate browsers accept.',
  'ssl.expired': 'Certificate expired — not trusted',
  'ssl.expiredHelp':
    'The certificate for {domains} has run out. Browsers will refuse it until a new one is issued. We renew automatically, so this usually means renewal has been failing — open this panel’s details or ask us.',
  'ssl.expires': 'Expires {when}',
  'ssl.daysRemaining': '{days} days left',
  'ssl.renewalScheduled': 'We renew it automatically before it expires.',
  'ssl.renewalDue': 'We are renewing it now.',
  'ssl.renewalExpired': 'This certificate has expired.',
  'ssl.replaceNote':
    'When a certificate arrives we install it on your panel and check the panel still works before finishing.',
  'ssl.getOne': 'Get a certificate',
  'ssl.domain': 'Your domain',
  'ssl.domainHelp': 'The address people will type. It must already point at your server.',
  'ssl.email': 'Email for expiry reminders',
  'ssl.emailHelp': 'The certificate authority uses this to warn you if renewal ever stops working.',
  'ssl.production': 'Issue a real certificate',
  'ssl.productionHelp':
    'Leave this off to rehearse first. Real certificates are limited to a few per domain per week, so a mistake is worth catching on a test one.',
  'ssl.request': 'Request it',
  'ssl.requested': 'We are getting the certificate. It usually takes a couple of minutes.',
  'ssl.needsDns':
    'We prove you own the domain by adding a DNS record, so we need a Cloudflare API token first.',
  'ssl.dnsToken': 'Cloudflare API token',
  'ssl.dnsTokenHelp':
    'A token with permission to edit DNS for your zone. We check it works, store it encrypted, and never show it again.',
  'ssl.dnsZone': 'Zone',
  'ssl.dnsSave': 'Save token',
  'ssl.dnsSaved': 'Saved. We checked it with Cloudflare and it works.',
  'ssl.dnsConfigured': 'A Cloudflare token is saved for this account.',

  'telegram.title': 'Telegram',
  'telegram.subtitle': 'Get told when something happens, without signing in.',
  'telegram.notConfigured':
    'Telegram is not set up on this installation of PanelTunnel, so there is nothing to connect to.',
  'telegram.safety':
    'The chat can ask for status and nothing else. It cannot run commands, change settings or reach your servers.',
  'telegram.link': 'Get a connection code',
  'telegram.linkHelp':
    'Open the bot, press Start, and send it this code. It works once, and expires {when}.',
  'telegram.code': 'Your connection code',
  'telegram.linked': 'Connected chats',
  'telegram.chatLabel': 'Chat ending {suffix}',
  'telegram.linkedAt': 'Connected {when}',
  'telegram.unlink': 'Disconnect',
  'telegram.unlinkConfirm': 'This chat will stop receiving anything about your account.',
  'telegram.unreachable': 'We cannot send to this chat any more. Reconnect it to start again.',
  'telegram.notifications': 'What to tell me about',
  'telegram.notificationsHelp': 'Everything is on unless you turn it off here.',
  'telegram.saved': 'Saved.',

  'event.deployment.completed': 'An installation finished',
  'event.deployment.failed': 'An installation did not work',
  'event.server.offline': 'A server stopped answering',
  'event.node.offline': 'One of your extra servers stopped answering',
  'event.tunnel.down': 'The connection between your servers went down',
  'event.tunnel.degraded': 'The connection between your servers got worse',
  'event.certificate.expiring': 'A security certificate is close to expiring',
  'event.backup.failed': 'A backup did not work',
  'event.disk.high_usage': 'A server is running out of space',

  'monitoring.autoRefresh': 'Check again automatically',
  'monitoring.autoOff': 'Only when I ask',
  'monitoring.auto30s': 'Every 30 seconds',
  'monitoring.auto1m': 'Every minute',
  'monitoring.auto5m': 'Every 5 minutes',
  'monitoring.lastChecked': 'These figures are from {when}.',
  'monitoring.refreshFailed':
    'We could not get fresh figures just now, so these are the last ones we did get. We will keep trying.',
  'monitoring.title': 'Monitoring',
  'monitoring.subtitle': 'How your servers, panels and connections are doing.',
  'monitoring.allHealthy': 'Everything is working.',
  'monitoring.needsAttention': '{count} things need attention.',
  'monitoring.servers': 'Servers',
  'monitoring.panels': 'Panels',
  'monitoring.tunnels': 'Connections between servers',
  'monitoring.nothing': 'There is nothing to monitor yet.',
  'monitoring.memory': 'Memory',
  'monitoring.disk': 'Disk',
  'monitoring.load': 'Busy',
  'monitoring.usedOf': '{used} of {total} used',
  'monitoring.latency': 'Round trip',
  'monitoring.packetLoss': 'Lost messages',
  'monitoring.nodes': '{count} servers connected',
  'monitoring.notInspected': 'We have not measured this server yet.',
  'monitoring.checkedAt': 'Checked {when}',

  'incidents.title': 'Problems',
  'incidents.none': 'Nothing has gone wrong.',
  'incidents.open': 'Happening now',
  'incidents.resolved': 'Fixed',
  'incidents.detected': 'Noticed {when}',
  'incidents.resolvedAt': 'Fixed {when}',
  'incidents.affected': 'Affects',
  'incidents.repairs': 'What we tried',
  'incidents.repairNone': 'We did not try to repair this automatically.',
  'incidents.technicalHidden':
    'Technical details are only shown to accounts with a developer or support role.',

  'repair.restart_panel_service': 'Restarted the panel',
  'repair.restart_tunnel_interface': 'Restarted the connection',
  'repair.rotate_tunnel_keys': 'Replaced the connection keys',
  'repair.lower_tunnel_mtu': 'Reduced the message size on the connection',
  'repair.free_disk_space': 'Freed up disk space',
  'repair.renew_certificate': 'Renewed the security certificate',
  'repair.enable_ip_forwarding': 'Re-enabled traffic forwarding',
  'repair.reinstall_panel': 'Reinstalled the panel',
  'repair.reboot_server': 'Restarted the server',
  'repair.none': 'Nothing we can do automatically',
  'repair.outcomeRepaired': 'worked',
  'repair.outcomeFailed': 'did not work',
  'repair.outcomeStillUnhealthy': 'did not fix it',
  'repair.outcomeEscalated': 'needs a person',

  // ---- who you are, in two separate statements -----------------------------
  //
  // Every Telegram customer is the owner of their own organization, so "owner"
  // on its own read as though they owned PanelTunnel. These are two questions
  // with two answers, and the interface asks both rather than picking one.
  'account.roleLabel': 'Account role',
  'account.levelLabel': 'Administrative level',
  'account.role.owner': 'Account owner',
  'account.role.developer': 'Developer',
  'account.role.support': 'Support',
  'account.role.customer': 'Customer',
  'account.level.tenant': 'This account only',
  'account.level.platform': 'Platform administrator',

  'nav.admin': 'Platform administration',

  'admin.title': 'Platform administration',
  'admin.subtitle': 'Everything on this installation, across every customer.',
  'admin.section.dashboard': 'Overview',
  'admin.section.customers': 'Customers',
  'admin.section.users': 'Users',
  'admin.section.servers': 'Servers',
  'admin.section.installations': 'Installations',
  'admin.section.tunnels': 'Tunnels',
  'admin.section.adapters': 'What we can install',
  'admin.section.backups': 'Backups',
  'admin.section.incidents': 'Problems',
  'admin.section.activity': 'Activity log',
  'admin.section.ai': 'AI settings',
  'admin.section.settings': 'System settings',

  'admin.adapters.explain':
    'Read from this installation itself, not from a list somebody keeps up to date. Anything not shown here cannot be installed by this build.',
  'admin.adapters.offered': 'Offered to customers',
  'admin.adapters.withheld': 'Built, and deliberately not offered yet',
  'admin.adapters.none': 'Nothing in this group.',
  'admin.adapters.verified': 'Upstream checked',
  'admin.adapters.caveats': 'Caveats shown to the customer before installing',

  'admin.metric.customers': 'Customers',
  'admin.metric.activeCustomers': 'Active this week',
  'admin.metric.suspended': 'Suspended',
  'admin.metric.telegramCustomers': 'Telegram accounts',
  'admin.metric.users': 'Users',
  'admin.metric.servers': 'Servers',
  'admin.metric.installations': 'Panel installations',
  'admin.metric.tunnels': 'Tunnels',
  'admin.metric.unhealthyTunnels': 'Tunnels not working',
  'admin.metric.failedDeployments': 'Failed installations',
  'admin.metric.incidents': 'Open problems',
  'admin.metric.backupFailures': 'Backup failures this week',
  'admin.metric.certificates': 'Certificates needing attention',
  'admin.metric.panelsByAdapter': 'Panels by type',
  'admin.metric.deploymentsByShape': 'Installations by kind',
  'admin.metric.serversByStatus': 'Servers by state',

  'admin.customers.search': 'Search by name, Telegram username or id',
  'admin.customers.telegramId': 'Telegram id',
  'admin.customers.username': 'Username',
  'admin.customers.created': 'Joined',
  'admin.customers.lastSeen': 'Last seen',
  'admin.customers.resources': 'Servers · installations · tunnels',
  'admin.customers.none': 'No customer matches that.',
  'admin.customers.open': 'Open',
  'admin.status.active': 'Active',
  'admin.status.suspended': 'Suspended',
  'admin.status.deploymentsPaused': 'Installations paused',
  'admin.status.never': 'Never',

  'admin.action.suspend': 'Suspend',
  'admin.action.reactivate': 'Reactivate',
  'admin.action.pauseDeployments': 'Pause new installations',
  'admin.action.resumeDeployments': 'Resume installations',
  'admin.action.revokeSessions': 'Sign this customer out everywhere',
  'admin.action.reason': 'Reason',
  'admin.action.reasonHelp':
    'Required, and kept. In a month this is the only thing that explains what happened.',
  'admin.action.done': 'Done.',
  'admin.action.recheck': 'Check their installation again',
  'admin.action.mute': 'Silence their notifications',
  'admin.action.unmute': 'Let their notifications through',
  'admin.action.recheckHelp':
    'Reads their machines and repairs what we already know how to repair. Installs nothing, removes nothing.',
  'admin.suspendNote':
    'Suspending stops this customer starting anything new. Everything they already have keeps running, keeps being backed up and keeps being monitored.',

  'admin.detail.servers': 'Servers',
  'admin.detail.deployments': 'Installations',
  'admin.detail.installations': 'Panels',
  'admin.detail.tunnels': 'Tunnels',
  'admin.detail.incidents': 'Problems',
  'admin.detail.audit': 'Recent activity',
  'admin.detail.noSecrets':
    'Passwords, keys and other secrets are not shown here and cannot be read from here.',

  'admin.settings.title': 'System settings',
  'admin.ai.title': 'Artificial intelligence',
  'admin.ai.help':
    'The local model on your AI server answers first and costs nothing per question. The fallback is only asked when the local one cannot answer.',
  'admin.ai.off': 'The AI is switched off. Everything works without it.',
  'admin.ai.noFallback': 'No fallback is configured, so nothing here can be billed.',
  'admin.ai.unavailable': 'Not answering right now',
  'admin.ai.available': 'Answering',
  'admin.ai.thisMonth': 'This month',
  'admin.ai.calls': 'Questions',
  'admin.ai.escalations': 'Escalated here',
  'admin.ai.tokens': 'Tokens',
  'admin.ai.cost': 'Estimated cost',
  'admin.ai.noPrice': 'No price configured',
  'admin.ai.nothingYet': 'Nothing has been asked this month.',
  'admin.ai.budgetCalls': 'Monthly limit on fallback questions',
  'admin.ai.budgetTokens': 'Monthly limit on fallback tokens',
  'admin.ai.budgetHelp':
    'Zero means no limit. Reaching a limit is not a fault: the local model goes on answering and everything keeps working.',
  'admin.ai.localTitle': 'Local AI server',
  'admin.ai.localHelp':
    'The machine that answers first. It is one you run, so a question costs nothing, and the platform works normally when it is unreachable.',
  'admin.ai.localEnabled': 'Ask the local model',
  'admin.ai.apiUrl': 'Gateway address',
  'admin.ai.apiUrlHelp':
    'Must be https unless it is on this machine: the diagnostics and the key would otherwise cross the network in the clear.',
  'admin.ai.apiKey': 'API key',
  'admin.ai.apiKeySet': 'A key is stored. Leave this empty to keep it.',
  'admin.ai.apiKeyUnset': 'No key is stored.',
  'admin.ai.apiKeyClear': 'Remove the stored key',
  'admin.ai.apiKeyNeverShown': 'A key is never shown again after it is saved.',
  'admin.ai.defaultModel': 'Default model',
  'admin.ai.taskModels': 'A model per job',
  'admin.ai.taskModelsHelp':
    'Leave a job empty to use the default. Diagnosing a server and writing a Persian reply are different jobs and can run on different models.',
  'admin.ai.claudeTitle': 'Claude (paid fallback)',
  'admin.ai.claudeHelp':
    'Asked only when the local model cannot answer. Off until you give it a key, because a fallback that turns itself on is one that costs money nobody agreed to.',
  'admin.ai.claudeEnabled': 'Use Claude when the local model cannot answer',
  'admin.ai.claudeModel': 'Model',
  'admin.ai.claudeEmergencyModel': 'Emergency model',
  'admin.ai.claudeEmergencyHelp':
    'Asked once when the first model is rate limited or answers with something unusable. Leave empty for one attempt only.',
  'admin.ai.maxOutputTokens': 'Longest answer, in tokens',
  'admin.ai.maxOutputTokensHelp': 'The ceiling on what one paid answer can cost.',
  'admin.ai.test': 'Test connection',
  'admin.ai.testing': 'Testing…',
  'admin.ai.testOk': 'Answered.',
  'admin.ai.testFailed': 'Did not answer',
  'admin.ai.save': 'Save AI settings',
  'admin.ai.saved': 'Saved. It is in use now — nothing needs restarting.',
  'admin.ai.badEndpoint':
    'That address cannot be used. It must be https unless it is on this machine.',
  'admin.settings.autonomy': 'How much we fix on our own',
  'admin.settings.autonomyHelp':
    'A ceiling on how risky an automatic repair may be. Anything above it is proposed and waits for you, and nothing that destroys data is ever automatic.',
  'admin.settings.autonomySafe': 'Safe Auto',
  'admin.settings.autonomySafeHelp':
    'Only changes nobody notices. Anything that interrupts service waits for you.',
  'admin.settings.autonomyAuto': 'Auto',
  'admin.settings.autonomyAutoHelp':
    'The default. Ordinary repairs happen on their own, including brief restarts.',
  'admin.settings.autonomyExpert': 'Expert',
  'admin.settings.autonomyExpertHelp':
    'Including reboots and replacing tunnel keys. Anything that destroys data still asks.',
  'admin.settings.telegramSignup': 'Allow new Telegram customers',
  'admin.settings.telegramSignupHelp':
    'Off stops new accounts being created. Existing customers keep working exactly as before.',
  'admin.settings.maintenance': 'Maintenance mode',
  'admin.settings.maintenanceHelp':
    'Stops anyone starting new work. Nothing already installed is touched.',
  'admin.settings.maintenanceMessage': 'What to tell customers',
  'admin.settings.serverLimit': 'Default server limit',
  'admin.settings.deploymentLimit': 'Default installation limit',
  'admin.settings.panels': 'Panels on offer',
  'admin.settings.tunnels': 'Tunnel methods on offer',
  'admin.settings.adaptersHelp':
    'Unticking one takes it out of the choices customers are offered. Nothing here can switch on something this platform does not have, or skip a check an adapter makes.',
  'admin.settings.notifications': 'Notification defaults',
  'admin.settings.notifyDeployment': 'Tell customers when an installation finishes',
  'admin.settings.notifyIncident': 'Tell customers when something breaks',
  'admin.settings.notifyBackup': 'Tell customers when a backup fails',
  'admin.settings.save': 'Save settings',
  'admin.settings.saved': 'Settings saved.',

  'settings.title': 'Settings',
  'settings.advanced': 'Advanced',
  'settings.expert': 'Show technical details',
  'settings.expertHelp':
    'Adds identifiers, measurements and the exact checks we ran to every screen. Nothing is hidden from you either way — this only decides how much is on screen.',

  'servers.cpuRam': 'Processor and memory',
  'servers.cpuRamValue': '{cpu} processors · {memory} GB memory',
  'servers.lastChecked': 'Last checked',

  'permission.cannot': 'Your account does not have permission for this.',
  'permission.readOnly':
    'Your account can see this but not change it. Ask an owner of this account if you need to.',

  'error.screenTitle': 'This page did not load properly',
  'error.screenBody':
    'Something went wrong while drawing this page. Nothing on your servers was affected.',

  'dashboard.everythingWorking': 'Everything is working.',
  'dashboard.needsAttention': '{count} things need your attention.',
  'dashboard.backupsOff': 'Scheduled backups are off for {count} of your panels.',
  'dashboard.bootstrapCertificate':
    '{count} of your panels is using an internal certificate that browsers do not accept.',
  'dashboard.openMonitoring': 'See details',
  'dashboard.openBackups': 'Set up backups',
  'dashboard.openSettings': 'Open settings',

  'wizard.review.stepsToggle': 'What we will do, step by step',
  'wizard.shape.onlyOne': 'This is the only setup that fits the servers you have.',

  'ai.title': 'AI assistance',
  'ai.disabled':
    'No AI service is configured, and nothing needs one. Installing, repairing, backing up and monitoring all work exactly the same without it.',
  'ai.configured': 'An AI service is configured at {endpoint}.',
  'ai.reachable': 'It answered when we last asked.',
  'ai.unreachable': 'It did not answer. Everything else carries on as normal.',
  'ai.neverRequired': 'AI is never required. It only ever explains; it cannot change your servers.',

  'severity.blocker': 'Must be fixed',
  'severity.warning': 'Worth knowing',
  'severity.info': 'For information',
} as const;

export type MessageKey = keyof typeof en;

export const fa: Record<MessageKey, string> = {
  'app.name': 'پنل‌تانل',
  'app.tagline': 'پنل و سرورهایتان را بدون کار با ترمینال راه‌اندازی کنید.',

  'nav.dashboard': 'خانه',
  'nav.servers': 'سرورهای من',
  'nav.deployments': 'نصب‌ها',
  'nav.newDeployment': 'نصب جدید',
  'nav.signOut': 'خروج',
  'nav.menu': 'منو',
  'nav.close': 'بستن منو',

  'common.next': 'ادامه',
  'common.back': 'بازگشت',
  'common.cancel': 'انصراف',
  'common.retry': 'تلاش دوباره',
  'common.loading': 'در حال بارگذاری…',
  'common.optional': 'اختیاری',
  'common.copy': 'کپی',
  'common.copied': 'کپی شد',
  'common.show': 'نمایش',
  'common.hide': 'پنهان',
  'common.somethingWentWrong': 'خطایی رخ داد.',
  'common.offline': 'به نظر می‌رسد اتصال اینترنت ندارید. تلاش را ادامه می‌دهیم.',

  'theme.label': 'ظاهر',
  'theme.light': 'روشن',
  'theme.dark': 'تیره',
  'theme.system': 'مطابق دستگاه من',
  'locale.label': 'زبان',

  'mode.label': 'حالت',
  'mode.easy': 'ساده',
  'mode.expert': 'پیشرفته',
  'mode.easyHelp': 'تنظیمات مناسب را ما برایتان انتخاب می‌کنیم.',
  'mode.expertHelp': 'پورت، تونل و جزئیات را خودتان انتخاب می‌کنید.',

  'auth.signIn': 'ورود',
  'auth.signUp': 'ساخت حساب',
  'auth.email': 'ایمیل',
  'auth.password': 'گذرواژه',
  'auth.displayName': 'نام شما',
  'auth.organizationName': 'نام شرکت یا تیم',
  'auth.haveAccount': 'حساب دارید؟',
  'auth.needAccount': 'تازه آمده‌اید؟',
  'auth.passwordHint': 'دست‌کم ۱۰ نویسه. یک جملهٔ کوتاه گزینهٔ خوبی است.',
  'auth.welcome': 'خوش آمدید',
  'auth.welcomeNew': 'بیایید شروع کنیم',

  'dashboard.greeting': 'سلام، {name}',
  'dashboard.noServers': 'هنوز سروری اضافه نکرده‌اید.',
  'dashboard.noServersHelp':
    'سروری را که اجاره کرده‌اید اضافه کنید؛ پیش از نصب هر چیزی آن را بررسی می‌کنیم.',
  'dashboard.addFirstServer': 'افزودن نخستین سرور',
  'dashboard.startInstall': 'نصب پنل',
  'dashboard.serversCount': '{count} سرور',
  'dashboard.recentDeployments': 'نصب‌های اخیر',
  'dashboard.nothingYet': 'هنوز چیزی نیست.',

  'servers.title': 'سرورهای من',
  'servers.add': 'افزودن سرور',
  'servers.name': 'نامی که به یاد می‌آورید',
  'servers.namePlaceholder': 'مثلاً سرور آلمان',
  'servers.host': 'آدرس',
  'servers.hostHelp': 'آدرس IP یا نام میزبانی که ارائه‌دهنده به شما داده است.',
  'servers.port': 'پورت SSH',
  'servers.username': 'نام کاربری',
  'servers.usernameHelp': 'معمولاً «root».',
  'servers.accessMethod': 'چگونه وارد شویم؟',
  'servers.accessKey': 'با کلید SSH',
  'servers.accessPassword': 'با گذرواژه',
  'servers.privateKey': 'کلید خصوصی',
  'servers.privateKeyHelp':
    'کل کلید را همراه با سطر نخست و پایانی جای‌گذاری کنید. ما آن را رمزنگاری‌شده نگه می‌داریم.',
  'servers.privateKeyPassphrase': 'عبارت عبور کلید',
  'servers.passwordLabel': 'گذرواژه',
  'servers.sudoPassword': 'گذرواژهٔ دستورهای مدیریتی',
  'servers.location': 'کجاست؟',
  'servers.locationPlaceholder': 'مثلاً فرانکفورت',
  'servers.authorization': 'این سرور از آنِ من است یا اجازهٔ مدیریت آن را دارم.',
  'servers.authorizationHelp':
    'ما تنها به سرورهایی وصل می‌شویم که بگویید از آنِ شماست. هر زمان بخواهید می‌توانید این اجازه را پس بگیرید.',
  'servers.checking': 'در حال بررسی…',
  'servers.checkAgain': 'بررسی دوباره',
  'servers.remove': 'پس‌گرفتن دسترسی',
  'servers.removeConfirm':
    'اطلاعات ورود این سرور را حذف می‌کنیم. هیچ‌چیزِ نصب‌شده روی آن پاک نمی‌شود.',
  'servers.removed': 'اطلاعات دسترسی آن سرور را حذف کردیم. هیچ‌چیزِ نصب‌شده روی آن حذف نشده است.',
  'servers.statusDraft': 'هنوز بررسی نشده',
  'servers.statusInspecting': 'در حال بررسی',
  'servers.statusReady': 'آماده',
  'servers.statusUnreachable': 'در دسترس نیست',
  'servers.statusRetired': 'دسترسی پس گرفته شد',
  'servers.statusInUse': 'در حال استفاده',
  'servers.behindNat': 'پشت آدرس مشترک (NAT)',
  'servers.existingSoftware': 'از پیش روی این سرور نصب است',
  'servers.rebootPending': 'این سرور در انتظار راه‌اندازی مجدد است. ما آن را ری‌استارت نمی‌کنیم.',
  'servers.cannotElevate':
    'این حساب نمی‌تواند دستورهای مدیریتی اجرا کند. نصب به این دسترسی نیاز دارد.',

  'wizard.title': 'نصب جدید',
  'wizard.step': 'گام {current} از {total}',
  'wizard.step.panel': 'انتخاب پنل',
  'wizard.step.shape': 'انتخاب چیدمان',
  'wizard.step.servers': 'تخصیص سرورها',
  'wizard.step.options': 'تنظیمات',
  'wizard.step.review': 'بازبینی',

  'wizard.panel.help': 'این همان چیزی است که وارد آن می‌شوید و کاربرانتان را مدیریت می‌کنید.',
  'wizard.panel.recommended': 'اگر مطمئن نیستید، {name} را انتخاب کنید.',
  'wizard.panel.multiNode': 'می‌تواند چند سرور را مدیریت کند',
  'wizard.panel.singleNode': 'تنها یک سرور',
  'wizard.panel.difficultyEasy': 'ساده‌ترین',
  'wizard.panel.difficultyModerate': 'متعادل',
  'wizard.panel.difficultyAdvanced': 'کامل‌ترین',
  'wizard.panel.needs': 'نیازمند {memory} مگابایت حافظه و {cpu} پردازنده',
  'wizard.panel.unavailableForShape': 'برای چیدمانی که انتخاب کرده‌اید در دسترس نیست',

  'wizard.shape.help': 'سرورهای شما چگونه با هم کار کنند؟',
  'wizard.shape.needsMoreServers': 'برای این چیدمان {count} سرور لازم است. شما {have} سرور دارید.',

  'wizard.servers.help':
    'هر سرور را در جای خود بگذارید. تنها سرورهایی را پیشنهاد می‌دهیم که بررسی کرده‌ایم.',
  'wizard.servers.choose': 'یک سرور انتخاب کنید',
  'wizard.servers.noneReady': 'هنوز بررسی هیچ‌یک از سرورهای شما کامل نشده است.',
  'wizard.servers.addMore': 'افزودن سرور دیگر',
  'wizard.servers.alreadyUsed': 'در این چیدمان استفاده شده است',

  'wizard.options.tunnel': 'سرورهای شما چگونه با یکدیگر ارتباط بگیرند؟',
  'wizard.options.tunnelAutomatic': 'خودتان انتخاب کنید (پیشنهادی)',
  'wizard.options.tunnelAutomaticHelp':
    'اتصال میان سرورهایتان را آزمایش می‌کنیم و بهترین گزینه را برمی‌گزینیم.',
  'wizard.options.domain': 'دامنهٔ شما',
  'wizard.options.domainHelp':
    'برای استفاده از آدرس سرور، این را خالی بگذارید. بعداً هم می‌توانید دامنه اضافه کنید.',
  'wizard.options.ssl': 'دریافت گواهی امنیتی برای دامنه',
  'wizard.options.panelPort': 'پورت پنل',
  'wizard.options.panelPortHelp': 'تا وقتی دلیل خاصی ندارید، این را تغییر ندهید.',
  'wizard.options.encryptedOnly': 'تنها از اتصال رمزنگاری‌شده میان سرورها استفاده شود',

  'wizard.review.title': 'این کاری است که انجام خواهیم داد',
  'wizard.review.estimated': 'حدود {minutes} دقیقه',
  'wizard.review.steps': '{count} گام',
  'wizard.review.tunnelChoice': 'اتصال میان سرورهای شما',
  'wizard.review.warnings': 'پیش از ادامه بخوانید',
  'wizard.review.findings': 'آنچه روی سرورهایتان یافتیم',
  'wizard.review.credentials': 'دریافت خواهید کرد',
  'wizard.review.approve': 'شروع نصب',
  'wizard.review.approving': 'در حال شروع…',
  'wizard.review.blocker':
    'تا رفع این مورد نمی‌توانیم ادامه دهیم. هیچ تغییری روی سرورهای شما انجام نشده است.',

  'progress.title': 'در حال آماده‌سازی همه‌چیز',
  'progress.subtitle': 'می‌توانید این صفحه را ببندید؛ کار ادامه پیدا می‌کند.',
  'progress.stepRunning': 'در حال انجام',
  'progress.stepSuccess': 'انجام شد',
  'progress.stepFailed': 'انجام نشد',
  'progress.stepPending': 'در انتظار',
  'progress.stepSkipped': 'لازم نبود',
  'progress.reconnecting': 'در حال اتصال دوباره…',
  'progress.cancel': 'توقف',
  'progress.cancelConfirm': 'پس از پایان گام جاری متوقف می‌شویم. آنچه نصب شده باقی می‌ماند.',
  'progress.cancelling': 'پس از گام جاری متوقف می‌شویم…',

  'result.successTitle': 'همه‌چیز آماده است',
  'result.successBody': 'پنل شما نصب شد و کار می‌کند. نحوهٔ ورود در ادامه آمده است.',
  'result.failedTitle': 'نتوانستیم کار را تمام کنیم',
  'result.failedBody':
    'به‌صورت ایمن متوقف شدیم و آنچه انجام داده بودیم را برگرداندیم. چیزی ناتمام باقی نماند.',
  'result.rolledBack': 'تغییرهای خود را برگرداندیم.',
  'result.openPanel': 'باز کردن پنل من',
  'result.credentials': 'اطلاعات ورود شما',
  'result.credentialsUnavailable':
    'نصب شما به پایان رسید، اما در این لحظه نتوانستیم اطلاعات ورود را بارگذاری کنیم. این اطلاعات امن است — چند لحظه بعد دوباره تلاش کنید. دوباره نصب نکنید.',
  'result.credentialWarning':
    'این‌ها را جایی امن ذخیره کنید. ما آن‌ها را رمزنگاری‌شده نگه می‌داریم، اما بهتر است نسخهٔ خودتان را داشته باشید.',
  'result.revealOnce': 'نمایش گذرواژه',
  'result.username': 'نام کاربری',
  'result.password': 'گذرواژه',
  'result.address': 'آدرس',
  'result.whatNext': 'حالا چه کنم؟',
  'result.whatNextBody': 'وارد پنل خود شوید و نخستین کاربرتان را بسازید.',

  'access.title': 'دسترسی پنل شما',
  'access.body':
    'این لینک فقط یک بار باز می‌شود. پیش از بستن این صفحه، این اطلاعات را جایی امن ذخیره کنید.',
  'access.expired':
    'این لینک قبلاً استفاده شده یا منقضی شده است. از ربات یک لینک تازه بخواهید — هر وقت لازم داشتید می‌تواند لینک جدید بسازد.',
  'access.loading': 'در حال باز کردن اطلاعات شما…',
  'access.none': 'برای این نصب اطلاعات ورودی ذخیره نشده است.',
  'access.notHeld':
    'این رمز در PanelTunnel نگهداری نمی‌شود — با همان رمزی که قبلاً استفاده می‌کردید وارد شوید.',
  'access.panelHost': 'سرور پنل',
  'access.via': 'مسیر دسترسی',
  'access.viaEntryRelay': 'از طریق رله ورودی {host} (فقط مسیر دسترسی؛ پنل روی آن سرور نصب نیست)',

  'deployments.title': 'نصب‌ها',
  'deployments.empty': 'هنوز چیزی نصب نکرده‌اید.',
  'deployments.statusDraft': 'پیش‌نویس',
  'deployments.statusPlanned': 'در انتظار تأیید شما',
  'deployments.statusQueued': 'در صف',
  'deployments.statusRunning': 'در حال انجام',
  'deployments.statusVerifying': 'بررسی درستی کار',
  'deployments.statusSucceeded': 'سالم',
  'deployments.statusFailed': 'انجام نشد',
  'deployments.statusCancelled': 'متوقف شد',
  'deployments.statusRolledBack': 'برگردانده شد',
  'deployments.statusPartiallyRolledBack': 'تا حدی برگردانده شد',
  'deployments.statusCancelling': 'در حال توقف',
  'deployments.view': 'مشاهده',

  'error.network': 'نتوانستیم به سرور برسیم. اتصال خود را بررسی و دوباره تلاش کنید.',
  'error.rateLimited': 'تلاش‌های بیش از حد. لطفاً یک دقیقه صبر کنید و دوباره تلاش کنید.',

  'nav.backups': 'پشتیبان‌ها',
  'nav.monitoring': 'پایش',
  'nav.settings': 'تنظیمات',

  'common.on': 'روشن',
  'common.off': 'خاموش',
  'common.never': 'هرگز',
  'common.unknown': 'نامشخص',
  'common.refresh': 'به‌روزرسانی',
  'common.disable': 'خاموش کردن',
  'common.technical': 'جزئیات فنی',

  'health.healthy': 'سالم',
  'health.degraded': 'کار می‌کند، با اشکال',
  'health.unhealthy': 'کار نمی‌کند',
  'health.unknown': 'هنوز بررسی نشده',
  'health.lastChecked': 'آخرین بررسی {when}',
  'health.neverChecked': 'هنوز این مورد را بررسی نکرده‌ایم.',

  'backups.title': 'پشتیبان‌ها',
  'backups.subtitle': 'نسخه‌ای رمزگذاری‌شده از کاربران و تنظیمات پنل شما.',
  'backups.optIn':
    'تا زمانی که خودتان روشن نکنید، پشتیبان‌گیری زمان‌بندی‌شده انجام نمی‌دهیم. هر وقت بخواهید می‌توانید یک نسخه بگیرید.',
  'backups.noPanels': 'هنوز پنلی نصب نشده که از آن پشتیبان بگیریم.',
  'backups.scheduleOff': 'پشتیبان‌گیری زمان‌بندی‌شده خاموش است',
  'backups.scheduleOn': 'هر {hours} ساعت یک پشتیبان گرفته می‌شود',
  'backups.nextRun': 'نوبت بعدی {when}',
  'backups.every': 'هر چند وقت',
  'backups.every6': 'هر ۶ ساعت',
  'backups.every12': 'هر ۱۲ ساعت',
  'backups.every24': 'روزی یک بار',
  'backups.every168': 'هفته‌ای یک بار',
  'backups.keep': 'چند نسخه نگه داریم',
  'backups.keepHelp': 'نسخه‌های قدیمی‌تر پس از یک پشتیبان‌گیری موفق حذف می‌شوند.',
  'backups.turnOnConfirm': 'روشن کردن پشتیبان‌گیری زمان‌بندی‌شده',
  'backups.turnOffConfirm':
    'دیگر به‌صورت زمان‌بندی‌شده پشتیبان نمی‌گیریم. نسخه‌های موجود نگه داشته می‌شوند.',
  'backups.now': 'اکنون پشتیبان بگیر',
  'backups.queued': 'در حال گرفتن پشتیبان هستیم. پس از پایان اینجا نمایش داده می‌شود.',
  'backups.history': 'پشتیبان‌های شما',
  'backups.empty': 'هنوز پشتیبانی وجود ندارد.',
  'backups.taken': 'زمان',
  'backups.size': 'حجم',
  'backups.reason': 'دلیل',
  'backups.reasonRequested': 'به درخواست شما',
  'backups.reasonScheduled': 'طبق زمان‌بندی',
  'backups.reasonRestore': 'پیش از بازگردانی',
  'backups.verify': 'بررسی سلامت',
  'backups.verifyHelp': 'پشتیبان را باز و کامل بودن آن را بررسی می‌کند. پنل شما دست نمی‌خورد.',
  'backups.verified': 'این پشتیبان کامل و قابل خواندن است.',
  'backups.verifyFailed': 'نتوانستیم این پشتیبان را بخوانیم: {reason}',
  'backups.restore': 'بازگردانی',
  'backups.restoreTitle': 'این پشتیبان بازگردانی شود؟',
  'backups.restoreWarning':
    'همهٔ محتوای پنل شما با نسخهٔ {when} جایگزین می‌شود. کاربرانی که پس از آن افزوده شده‌اند از بین می‌روند. پیش از کار یک پشتیبان می‌گیریم، اما جز آن راه بازگشتی نیست.',
  'backups.restoreType': 'برای تأیید RESTORE را بنویسید',
  'backups.restoreWord': 'RESTORE',
  'backups.restoreStarted': 'بازگردانی آغاز شد. می‌توانید در همین صفحه دنبالش کنید.',

  'ssl.title': 'گواهی امنیتی',
  'ssl.subtitle': 'آنچه مرورگر هنگام باز کردن پنل شما می‌بیند.',
  'ssl.trusted': 'گواهی معتبر',
  'ssl.trustedHelp': 'برای {domains} صادر شده است. مرورگرها بدون هشدار آن را می‌پذیرند.',
  'ssl.bootstrap': 'گواهی داخلی — معتبر نیست',
  'ssl.bootstrapHelp':
    'پنل شما از گواهی‌ای استفاده می‌کند که خودمان ساخته‌ایم تا پنل بتواند بالا بیاید. مرورگرها آن را نمی‌پذیرند و هشدار نشان می‌دهند. برای دسترسی از طریق ارتباط خصوصی میان سرورها مشکلی ندارد؛ برای یک آدرس عمومی مناسب نیست.',
  'ssl.bootstrapAction': 'دامنه‌تان را اضافه کنید تا گواهی‌ای بگیریم که مرورگرها بپذیرند.',
  'ssl.expired': 'گواهی منقضی شده — معتبر نیست',
  'ssl.expiredHelp':
    'گواهی {domains} منقضی شده است. تا صدور گواهی تازه، مرورگرها آن را نمی‌پذیرند. تمدید به‌صورت خودکار انجام می‌شود، پس این معمولاً یعنی تمدید ناموفق بوده است — جزئیات این پنل را ببینید یا با ما تماس بگیرید.',
  'ssl.expires': 'انقضا {when}',
  'ssl.daysRemaining': '{days} روز باقی مانده',
  'ssl.renewalScheduled': 'پیش از انقضا به‌صورت خودکار تمدیدش می‌کنیم.',
  'ssl.renewalDue': 'هم‌اکنون در حال تمدید آن هستیم.',
  'ssl.renewalExpired': 'این گواهی منقضی شده است.',
  'ssl.replaceNote':
    'وقتی گواهی صادر شد، آن را روی پنل نصب می‌کنیم و پیش از پایان کار بررسی می‌کنیم که پنل همچنان کار می‌کند.',
  'ssl.getOne': 'دریافت گواهی',
  'ssl.domain': 'دامنهٔ شما',
  'ssl.domainHelp': 'آدرسی که افراد وارد می‌کنند. باید از پیش به سرور شما اشاره کند.',
  'ssl.email': 'ایمیل برای یادآوری انقضا',
  'ssl.emailHelp': 'مرجع صدور گواهی با این ایمیل هشدار می‌دهد اگر تمدید از کار بیفتد.',
  'ssl.production': 'صدور گواهی واقعی',
  'ssl.productionHelp':
    'برای تمرین، این را خاموش بگذارید. گواهی‌های واقعی برای هر دامنه در هفته محدودند، پس بهتر است اشتباه‌ها روی نسخهٔ آزمایشی پیدا شوند.',
  'ssl.request': 'درخواست',
  'ssl.requested': 'در حال گرفتن گواهی هستیم. معمولاً چند دقیقه طول می‌کشد.',
  'ssl.needsDns':
    'مالکیت دامنه را با افزودن یک رکورد DNS اثبات می‌کنیم، پس نخست به یک توکن API کلودفلر نیاز داریم.',
  'ssl.dnsToken': 'توکن API کلودفلر',
  'ssl.dnsTokenHelp':
    'توکنی با اجازهٔ ویرایش DNS برای زون شما. کارکردش را بررسی می‌کنیم، رمزگذاری‌شده ذخیره‌اش می‌کنیم و دیگر هرگز نشانش نمی‌دهیم.',
  'ssl.dnsZone': 'زون',
  'ssl.dnsSave': 'ذخیرهٔ توکن',
  'ssl.dnsSaved': 'ذخیره شد. با کلودفلر بررسی کردیم و کار می‌کند.',
  'ssl.dnsConfigured': 'یک توکن کلودفلر برای این حساب ذخیره شده است.',

  'telegram.title': 'تلگرام',
  'telegram.subtitle': 'بدون ورود به حساب، از رویدادها باخبر شوید.',
  'telegram.notConfigured':
    'تلگرام روی این نصب پنل‌تانل راه‌اندازی نشده است، پس چیزی برای اتصال وجود ندارد.',
  'telegram.safety':
    'این گفت‌وگو تنها می‌تواند وضعیت را بپرسد. نمی‌تواند فرمانی اجرا کند، تنظیمی را تغییر دهد یا به سرورهای شما دسترسی پیدا کند.',
  'telegram.link': 'دریافت کد اتصال',
  'telegram.linkHelp':
    'ربات را باز کنید، Start را بزنید و همین کد را برایش بفرستید. یک‌بار مصرف است و {when} منقضی می‌شود.',
  'telegram.code': 'کد اتصال شما',
  'telegram.linked': 'گفت‌وگوهای متصل',
  'telegram.chatLabel': 'گفت‌وگوی پایان‌یافته به {suffix}',
  'telegram.linkedAt': 'متصل شده در {when}',
  'telegram.unlink': 'قطع اتصال',
  'telegram.unlinkConfirm': 'این گفت‌وگو دیگر چیزی دربارهٔ حساب شما دریافت نمی‌کند.',
  'telegram.unreachable':
    'دیگر نمی‌توانیم به این گفت‌وگو پیام بفرستیم. برای شروع دوباره متصلش کنید.',
  'telegram.notifications': 'دربارهٔ چه چیزهایی خبر بدهیم',
  'telegram.notificationsHelp': 'همه روشن است مگر اینکه اینجا خاموشش کنید.',
  'telegram.saved': 'ذخیره شد.',

  'event.deployment.completed': 'یک نصب به پایان رسید',
  'event.deployment.failed': 'یک نصب انجام نشد',
  'event.server.offline': 'یک سرور پاسخ نمی‌دهد',
  'event.node.offline': 'یکی از سرورهای دیگر شما پاسخ نمی‌دهد',
  'event.tunnel.down': 'ارتباط میان سرورهای شما قطع شد',
  'event.tunnel.degraded': 'کیفیت ارتباط میان سرورهای شما افت کرد',
  'event.certificate.expiring': 'یک گواهی امنیتی نزدیک به انقضاست',
  'event.backup.failed': 'یک پشتیبان‌گیری انجام نشد',
  'event.disk.high_usage': 'فضای یک سرور رو به پایان است',

  'monitoring.autoRefresh': 'بررسی خودکار دوباره',
  'monitoring.autoOff': 'فقط وقتی خودم بخواهم',
  'monitoring.auto30s': 'هر ۳۰ ثانیه',
  'monitoring.auto1m': 'هر دقیقه',
  'monitoring.auto5m': 'هر ۵ دقیقه',
  'monitoring.lastChecked': 'این اعداد مربوط به {when} است.',
  'monitoring.refreshFailed':
    'در این لحظه نتوانستیم اعداد تازه بگیریم؛ این‌ها آخرین اعدادی است که گرفته‌ایم. تلاش را ادامه می‌دهیم.',
  'monitoring.title': 'پایش',
  'monitoring.subtitle': 'وضعیت سرورها، پنل‌ها و ارتباط‌های شما.',
  'monitoring.allHealthy': 'همه چیز کار می‌کند.',
  'monitoring.needsAttention': '{count} مورد نیاز به بررسی دارد.',
  'monitoring.servers': 'سرورها',
  'monitoring.panels': 'پنل‌ها',
  'monitoring.tunnels': 'ارتباط میان سرورها',
  'monitoring.nothing': 'هنوز چیزی برای پایش وجود ندارد.',
  'monitoring.memory': 'حافظه',
  'monitoring.disk': 'دیسک',
  'monitoring.load': 'میزان کار',
  'monitoring.usedOf': '{used} از {total} استفاده شده',
  'monitoring.latency': 'زمان رفت و برگشت',
  'monitoring.packetLoss': 'پیام‌های از دست رفته',
  'monitoring.nodes': '{count} سرور متصل',
  'monitoring.notInspected': 'هنوز این سرور را اندازه‌گیری نکرده‌ایم.',
  'monitoring.checkedAt': 'بررسی‌شده {when}',

  'incidents.title': 'مشکل‌ها',
  'incidents.none': 'هیچ مشکلی پیش نیامده است.',
  'incidents.open': 'هم‌اکنون در جریان',
  'incidents.resolved': 'برطرف شده',
  'incidents.detected': 'شناسایی‌شده {when}',
  'incidents.resolvedAt': 'برطرف‌شده {when}',
  'incidents.affected': 'مربوط به',
  'incidents.repairs': 'آنچه تلاش کردیم',
  'incidents.repairNone': 'برای این مورد تلاش خودکاری برای ترمیم انجام ندادیم.',
  'incidents.technicalHidden':
    'جزئیات فنی تنها به حساب‌هایی با نقش توسعه‌دهنده یا پشتیبانی نشان داده می‌شود.',

  'repair.restart_panel_service': 'پنل را دوباره راه‌اندازی کردیم',
  'repair.restart_tunnel_interface': 'ارتباط را دوباره راه‌اندازی کردیم',
  'repair.rotate_tunnel_keys': 'کلیدهای ارتباط را تعویض کردیم',
  'repair.lower_tunnel_mtu': 'اندازهٔ پیام‌های ارتباط را کاهش دادیم',
  'repair.free_disk_space': 'فضای دیسک را آزاد کردیم',
  'repair.renew_certificate': 'گواهی امنیتی را تمدید کردیم',
  'repair.enable_ip_forwarding': 'هدایت ترافیک را دوباره فعال کردیم',
  'repair.reinstall_panel': 'پنل را دوباره نصب کردیم',
  'repair.reboot_server': 'سرور را دوباره راه‌اندازی کردیم',
  'repair.none': 'کاری نیست که به‌صورت خودکار انجام دهیم',
  'repair.outcomeRepaired': 'جواب داد',
  'repair.outcomeFailed': 'انجام نشد',
  'repair.outcomeStillUnhealthy': 'مشکل را برطرف نکرد',
  'repair.outcomeEscalated': 'نیاز به بررسی انسانی دارد',

  'account.roleLabel': 'نقش حساب',
  'account.levelLabel': 'سطح مدیریتی',
  'account.role.owner': 'مالک حساب',
  'account.role.developer': 'توسعه‌دهنده',
  'account.role.support': 'پشتیبانی',
  'account.role.customer': 'مشتری',
  'account.level.tenant': 'فقط همین حساب',
  'account.level.platform': 'مدیر کل پلتفرم',

  'nav.admin': 'مدیریت کل',

  'admin.title': 'مدیریت کل',
  'admin.subtitle': 'همهٔ چیزی که روی این نصب وجود دارد، برای همهٔ مشتری‌ها.',
  'admin.section.dashboard': 'داشبورد مدیریت',
  'admin.section.customers': 'مشتری‌ها',
  'admin.section.users': 'کاربران',
  'admin.section.servers': 'سرورها',
  'admin.section.installations': 'نصب‌ها',
  'admin.section.tunnels': 'تانل‌ها',
  'admin.section.adapters': 'آنچه می‌توانیم نصب کنیم',
  'admin.section.backups': 'بکاپ‌ها',
  'admin.section.incidents': 'خطاها',
  'admin.section.activity': 'گزارش فعالیت',
  'admin.section.ai': 'تنظیمات هوش مصنوعی',
  'admin.section.settings': 'تنظیمات سیستم',

  'admin.adapters.explain':
    'این فهرست از خود همین نصب خوانده می‌شود، نه از سیاهه‌ای که کسی باید به‌روز نگه دارد. هر چیزی که اینجا نیست، با این نسخه قابل نصب نیست.',
  'admin.adapters.offered': 'در دسترس مشتری‌ها',
  'admin.adapters.withheld': 'ساخته شده و هنوز عمداً ارائه نمی‌شود',
  'admin.adapters.none': 'در این گروه چیزی نیست.',
  'admin.adapters.verified': 'بررسی منبع اصلی',
  'admin.adapters.caveats': 'هشدارهایی که پیش از نصب به مشتری نشان داده می‌شود',

  'admin.metric.customers': 'مشتری‌ها',
  'admin.metric.activeCustomers': 'فعال در این هفته',
  'admin.metric.suspended': 'معلق',
  'admin.metric.telegramCustomers': 'حساب‌های تلگرامی',
  'admin.metric.users': 'کاربران',
  'admin.metric.servers': 'سرورها',
  'admin.metric.installations': 'پنل‌های نصب‌شده',
  'admin.metric.tunnels': 'تانل‌ها',
  'admin.metric.unhealthyTunnels': 'تانل‌های خراب',
  'admin.metric.failedDeployments': 'نصب‌های ناموفق',
  'admin.metric.incidents': 'مشکل‌های باز',
  'admin.metric.backupFailures': 'بکاپ‌های ناموفق این هفته',
  'admin.metric.certificates': 'گواهی‌های نیازمند رسیدگی',
  'admin.metric.panelsByAdapter': 'پنل‌ها بر اساس نوع',
  'admin.metric.deploymentsByShape': 'نصب‌ها بر اساس نوع',
  'admin.metric.serversByStatus': 'سرورها بر اساس وضعیت',

  'admin.customers.search': 'جست‌وجو با نام، نام کاربری تلگرام یا شناسه',
  'admin.customers.telegramId': 'شناسهٔ تلگرام',
  'admin.customers.username': 'نام کاربری',
  'admin.customers.created': 'تاریخ عضویت',
  'admin.customers.lastSeen': 'آخرین فعالیت',
  'admin.customers.resources': 'سرور · نصب · تانل',
  'admin.customers.none': 'مشتری‌ای با این مشخصات پیدا نشد.',
  'admin.customers.open': 'مشاهده',
  'admin.status.active': 'فعال',
  'admin.status.suspended': 'معلق',
  'admin.status.deploymentsPaused': 'نصب‌های جدید متوقف',
  'admin.status.never': 'هرگز',

  'admin.action.suspend': 'تعلیق حساب',
  'admin.action.reactivate': 'فعال‌سازی دوباره',
  'admin.action.pauseDeployments': 'توقف نصب‌های جدید',
  'admin.action.resumeDeployments': 'ازسرگیری نصب‌ها',
  'admin.action.revokeSessions': 'خروج این مشتری از همهٔ دستگاه‌ها',
  'admin.action.reason': 'دلیل',
  'admin.action.reasonHelp':
    'الزامی است و نگهداری می‌شود. یک ماه دیگر، این تنها چیزی است که توضیح می‌دهد چه اتفاقی افتاده.',
  'admin.action.done': 'انجام شد.',
  'admin.action.recheck': 'بررسی دوبارهٔ نصب این مشتری',
  'admin.action.mute': 'بی‌صدا کردن اعلان‌های این مشتری',
  'admin.action.unmute': 'باصدا کردن اعلان‌های این مشتری',
  'admin.action.recheckHelp':
    'سرورهای این مشتری را می‌خواند و چیزهایی را که از قبل می‌دانیم چطور ترمیم کنیم ترمیم می‌کند. چیزی نصب نمی‌کند و چیزی حذف نمی‌کند.',
  'admin.suspendNote':
    'تعلیق فقط جلوی شروع کار جدید را می‌گیرد. هر چیزی که این مشتری از قبل دارد همچنان کار می‌کند، بکاپ گرفته می‌شود و پایش می‌شود.',

  'admin.detail.servers': 'سرورها',
  'admin.detail.deployments': 'نصب‌ها',
  'admin.detail.installations': 'پنل‌ها',
  'admin.detail.tunnels': 'تانل‌ها',
  'admin.detail.incidents': 'مشکل‌ها',
  'admin.detail.audit': 'فعالیت اخیر',
  'admin.detail.noSecrets':
    'رمزها، کلیدها و دیگر اطلاعات محرمانه اینجا نمایش داده نمی‌شوند و از اینجا قابل خواندن نیستند.',

  'admin.settings.title': 'تنظیمات سیستم',
  'admin.ai.title': 'هوش مصنوعی',
  'admin.ai.help':
    'مدل محلی روی سرور هوش مصنوعی شما اول پاسخ می‌دهد و هزینه‌ای به ازای هر پرسش ندارد. مدل پشتیبان فقط وقتی پرسیده می‌شود که مدل محلی نتواند پاسخ دهد.',
  'admin.ai.off': 'هوش مصنوعی خاموش است. همه‌چیز بدون آن کار می‌کند.',
  'admin.ai.noFallback':
    'مدل پشتیبانی پیکربندی نشده است، بنابراین هزینه‌ای در اینجا ایجاد نمی‌شود.',
  'admin.ai.unavailable': 'در حال حاضر پاسخ نمی‌دهد',
  'admin.ai.available': 'در حال پاسخ‌گویی',
  'admin.ai.thisMonth': 'این ماه',
  'admin.ai.calls': 'پرسش‌ها',
  'admin.ai.escalations': 'ارجاع‌شده به اینجا',
  'admin.ai.tokens': 'توکن‌ها',
  'admin.ai.cost': 'هزینهٔ تخمینی',
  'admin.ai.noPrice': 'قیمتی تعیین نشده است',
  'admin.ai.nothingYet': 'این ماه چیزی پرسیده نشده است.',
  'admin.ai.budgetCalls': 'سقف ماهانهٔ پرسش از مدل پشتیبان',
  'admin.ai.budgetTokens': 'سقف ماهانهٔ توکن مدل پشتیبان',
  'admin.ai.budgetHelp':
    'صفر یعنی بدون محدودیت. رسیدن به سقف خطا نیست: مدل محلی به پاسخ‌گویی ادامه می‌دهد و همه‌چیز کار می‌کند.',
  'admin.ai.localTitle': 'سرور هوش مصنوعی محلی',
  'admin.ai.localHelp':
    'ماشینی که اول پاسخ می‌دهد. چون خودتان آن را اجرا می‌کنید، هر پرسش هزینه‌ای ندارد و اگر در دسترس نباشد پلتفرم عادی کار می‌کند.',
  'admin.ai.localEnabled': 'از مدل محلی بپرس',
  'admin.ai.apiUrl': 'نشانی درگاه',
  'admin.ai.apiUrlHelp':
    'باید https باشد مگر روی همین دستگاه: در غیر این صورت تشخیص‌ها و کلید بدون رمز از شبکه عبور می‌کنند.',
  'admin.ai.apiKey': 'کلید API',
  'admin.ai.apiKeySet': 'یک کلید ذخیره شده است. برای حفظ آن، این فیلد را خالی بگذارید.',
  'admin.ai.apiKeyUnset': 'کلیدی ذخیره نشده است.',
  'admin.ai.apiKeyClear': 'حذف کلید ذخیره‌شده',
  'admin.ai.apiKeyNeverShown': 'کلید پس از ذخیره دیگر هرگز نمایش داده نمی‌شود.',
  'admin.ai.defaultModel': 'مدل پیش‌فرض',
  'admin.ai.taskModels': 'یک مدل برای هر کار',
  'admin.ai.taskModelsHelp':
    'هر کاری را که خالی بگذارید از مدل پیش‌فرض استفاده می‌کند. بررسی سرور و نوشتن پاسخ فارسی دو کار متفاوت‌اند و می‌توانند روی دو مدل اجرا شوند.',
  'admin.ai.claudeTitle': 'Claude (پشتیبان پولی)',
  'admin.ai.claudeHelp':
    'فقط وقتی پرسیده می‌شود که مدل محلی نتواند پاسخ دهد. تا وقتی کلید ندهید خاموش است، چون پشتیبانی که خودش روشن شود هزینه‌ای است که کسی نپذیرفته.',
  'admin.ai.claudeEnabled': 'وقتی مدل محلی نتوانست پاسخ دهد از Claude استفاده کن',
  'admin.ai.claudeModel': 'مدل',
  'admin.ai.claudeEmergencyModel': 'مدل اضطراری',
  'admin.ai.claudeEmergencyHelp':
    'یک بار پرسیده می‌شود، وقتی مدل اول به سقف نرخ برسد یا پاسخ غیرقابل‌استفاده بدهد. برای تنها یک تلاش، خالی بگذارید.',
  'admin.ai.maxOutputTokens': 'بلندترین پاسخ، بر حسب توکن',
  'admin.ai.maxOutputTokensHelp': 'سقف هزینهٔ یک پاسخ پولی.',
  'admin.ai.test': 'آزمایش اتصال',
  'admin.ai.testing': 'در حال آزمایش…',
  'admin.ai.testOk': 'پاسخ داد.',
  'admin.ai.testFailed': 'پاسخ نداد',
  'admin.ai.save': 'ذخیرهٔ تنظیمات هوش مصنوعی',
  'admin.ai.saved': 'ذخیره شد. همین حالا اعمال شد — نیازی به راه‌اندازی مجدد نیست.',
  'admin.ai.badEndpoint': 'این نشانی قابل استفاده نیست. باید https باشد مگر روی همین دستگاه.',
  'admin.settings.autonomy': 'چه اندازه خودمان مشکل را برطرف کنیم',
  'admin.settings.autonomyHelp':
    'سقفی برای میزان خطرِ یک تعمیر خودکار. هر چیزی بالاتر از آن پیشنهاد می‌شود و منتظر تأیید شما می‌ماند، و هیچ کاری که داده را از بین ببرد خودکار انجام نمی‌شود.',
  'admin.settings.autonomySafe': 'خودکار محتاط',
  'admin.settings.autonomySafeHelp':
    'فقط تغییرهایی که کسی متوجه آن‌ها نمی‌شود. هر کاری که سرویس را قطع کند منتظر تأیید شما می‌ماند.',
  'admin.settings.autonomyAuto': 'خودکار',
  'admin.settings.autonomyAutoHelp':
    'حالت پیش‌فرض. تعمیرهای معمول، از جمله راه‌اندازی مجدد کوتاه، خودبه‌خود انجام می‌شوند.',
  'admin.settings.autonomyExpert': 'حرفه‌ای',
  'admin.settings.autonomyExpertHelp':
    'شامل راه‌اندازی مجدد سرور و تعویض کلیدهای تونل. هر کاری که داده را از بین ببرد همچنان تأیید می‌خواهد.',
  'admin.settings.telegramSignup': 'اجازهٔ ساخت حساب جدید در تلگرام',
  'admin.settings.telegramSignupHelp':
    'خاموش‌کردن آن جلوی ساخت حساب جدید را می‌گیرد. مشتری‌های فعلی دقیقاً مثل قبل کار می‌کنند.',
  'admin.settings.maintenance': 'حالت تعمیرات',
  'admin.settings.maintenanceHelp':
    'جلوی شروع کار جدید توسط همه را می‌گیرد. به هیچ چیز نصب‌شده‌ای دست نمی‌زند.',
  'admin.settings.maintenanceMessage': 'پیامی که به مشتری‌ها نشان داده می‌شود',
  'admin.settings.serverLimit': 'سقف پیش‌فرض سرور',
  'admin.settings.deploymentLimit': 'سقف پیش‌فرض نصب',
  'admin.settings.panels': 'پنل‌های قابل ارائه',
  'admin.settings.tunnels': 'روش‌های تانل قابل ارائه',
  'admin.settings.adaptersHelp':
    'برداشتن تیک هر کدام، آن را از انتخاب‌های مشتری حذف می‌کند. هیچ‌کدام از این تنظیمات نمی‌تواند چیزی را که این پلتفرم ندارد روشن کند یا بررسی‌های ایمنی یک آداپتور را نادیده بگیرد.',
  'admin.settings.notifications': 'پیش‌فرض‌های اعلان',
  'admin.settings.notifyDeployment': 'اطلاع به مشتری در پایان نصب',
  'admin.settings.notifyIncident': 'اطلاع به مشتری هنگام بروز مشکل',
  'admin.settings.notifyBackup': 'اطلاع به مشتری هنگام شکست بکاپ',
  'admin.settings.save': 'ذخیرهٔ تنظیمات',
  'admin.settings.saved': 'تنظیمات ذخیره شد.',

  'settings.title': 'تنظیمات',
  'settings.advanced': 'پیشرفته',
  'settings.expert': 'نمایش جزئیات فنی',
  'settings.expertHelp':
    'شناسه‌ها، اندازه‌گیری‌ها و بررسی‌های دقیقی که انجام داده‌ایم را به همهٔ صفحه‌ها می‌افزاید. در هر حال چیزی از شما پنهان نیست — این فقط تعیین می‌کند چه مقدار روی صفحه بیاید.',

  'servers.cpuRam': 'پردازنده و حافظه',
  'servers.cpuRamValue': '{cpu} پردازنده · {memory} گیگابایت حافظه',
  'servers.lastChecked': 'آخرین بررسی',

  'permission.cannot': 'حساب شما اجازهٔ این کار را ندارد.',
  'permission.readOnly':
    'حساب شما می‌تواند این را ببیند اما نمی‌تواند تغییرش دهد. در صورت نیاز از مالک این حساب بخواهید.',

  'error.screenTitle': 'این صفحه درست بارگذاری نشد',
  'error.screenBody':
    'هنگام ترسیم این صفحه خطایی رخ داد. هیچ تغییری روی سرورهای شما ایجاد نشده است.',

  'dashboard.everythingWorking': 'همه چیز کار می‌کند.',
  'dashboard.needsAttention': '{count} مورد نیاز به بررسی شما دارد.',
  'dashboard.backupsOff': 'پشتیبان‌گیری زمان‌بندی‌شده برای {count} پنل شما خاموش است.',
  'dashboard.bootstrapCertificate':
    '{count} پنل شما از گواهی داخلی استفاده می‌کند که مرورگرها آن را نمی‌پذیرند.',
  'dashboard.openMonitoring': 'مشاهدهٔ جزئیات',
  'dashboard.openBackups': 'راه‌اندازی پشتیبان‌گیری',
  'dashboard.openSettings': 'باز کردن تنظیمات',

  'wizard.review.stepsToggle': 'آنچه انجام می‌دهیم، گام به گام',
  'wizard.shape.onlyOne': 'با سرورهایی که دارید، تنها همین چیدمان ممکن است.',

  'ai.title': 'دستیار هوش مصنوعی',
  'ai.disabled':
    'هیچ سرویس هوش مصنوعی پیکربندی نشده و به آن نیازی هم نیست. نصب، ترمیم، پشتیبان‌گیری و پایش، همگی بدون آن دقیقاً مثل قبل کار می‌کنند.',
  'ai.configured': 'یک سرویس هوش مصنوعی روی {endpoint} پیکربندی شده است.',
  'ai.reachable': 'در آخرین بررسی پاسخ داد.',
  'ai.unreachable': 'پاسخ نداد. بقیهٔ بخش‌ها مثل همیشه کار می‌کنند.',
  'ai.neverRequired':
    'هوش مصنوعی هرگز الزامی نیست. تنها توضیح می‌دهد و نمی‌تواند چیزی را روی سرورهای شما تغییر دهد.',

  'severity.blocker': 'باید برطرف شود',
  'severity.warning': 'خوب است بدانید',
  'severity.info': 'برای اطلاع',
};

export const MESSAGES = { en, fa } as const;
export type Locale = keyof typeof MESSAGES;
export const LOCALES: readonly Locale[] = ['en', 'fa'];
/** Languages written right to left. Drives `dir`, never the layout code. */
export const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(['fa']);
